import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Calculator, TrendingUp, Home, DollarSign, MapPin, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const PriceComparison = ({ initialData, onPriceSelect }) => {
  const [loading, setLoading] = useState(false);
  const [compData, setCompData] = useState(null);
  const [formData, setFormData] = useState({
    address: initialData?.address || "",
    zipcode: initialData?.zipcode || "",
    sqft: initialData?.sqft || "",
    beds: initialData?.beds || "",
    baths: initialData?.baths || ""
  });

  const handleAnalyze = async () => {
    if (!formData.address || !formData.zipcode || !formData.sqft) {
      toast.error("Please fill in address, zipcode, and square footage");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/price-comparison`, {
        address: formData.address,
        zipcode: formData.zipcode,
        sqft: parseFloat(formData.sqft),
        beds: formData.beds ? parseInt(formData.beds) : null,
        baths: formData.baths ? parseFloat(formData.baths) : null
      });

      setCompData(response.data);
      toast.success("Price analysis complete!");
    } catch (error) {
      console.error("Price comparison error:", error);
      toast.error("Failed to analyze pricing. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleUsePrice = (price) => {
    if (onPriceSelect) {
      onPriceSelect(price);
      toast.success(`Price set to $${price.toLocaleString()}`);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-3 bg-orange-600 rounded-lg">
            <Calculator className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Smart Price Analyzer</h3>
            <p className="text-sm text-gray-600">Compare your home to recent sales nearby</p>
          </div>
        </div>

        {/* Input Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Street Address
            </label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              placeholder="123 Main St"
              className="border-orange-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ZIP Code
            </label>
            <Input
              value={formData.zipcode}
              onChange={(e) => setFormData({...formData, zipcode: e.target.value})}
              placeholder="75201"
              className="border-orange-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Home className="w-4 h-4 inline mr-1" />
              Square Feet
            </label>
            <Input
              type="number"
              value={formData.sqft}
              onChange={(e) => setFormData({...formData, sqft: e.target.value})}
              placeholder="1800"
              className="border-orange-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Beds / Baths
            </label>
            <div className="flex space-x-2">
              <Input
                type="number"
                value={formData.beds}
                onChange={(e) => setFormData({...formData, beds: e.target.value})}
                placeholder="3"
                className="border-orange-200"
              />
              <Input
                type="number"
                step="0.5"
                value={formData.baths}
                onChange={(e) => setFormData({...formData, baths: e.target.value})}
                placeholder="2"
                className="border-orange-200"
              />
            </div>
          </div>
        </div>

        <Button
          onClick={handleAnalyze}
          disabled={loading}
          className="w-full bg-orange-600 hover:bg-orange-700 text-white"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              Analyzing Market...
            </>
          ) : (
            <>
              <TrendingUp className="w-4 h-4 mr-2" />
              Analyze Market Price
            </>
          )}
        </Button>

        {compData?.using_mock_data && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start space-x-2">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              {compData.message}
            </p>
          </div>
        )}
      </Card>

      {/* Results */}
      {compData && (
        <div className="space-y-4">
          {/* Price Recommendation */}
          <Card className="p-6 border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
            <div className="text-center mb-4">
              <h4 className="text-lg font-semibold text-gray-700 mb-2">Recommended Listing Price</h4>
              <div className="text-5xl font-bold text-green-700 mb-2">
                ${compData.market_analysis.your_home.estimated_value.toLocaleString()}
              </div>
              <p className="text-sm text-gray-600">
                Based on {compData.market_analysis.total_comps_found} comparable sales within {compData.market_analysis.radius_miles} miles
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-white rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Conservative</p>
                <p className="text-lg font-bold text-gray-800">
                  ${compData.market_analysis.your_home.price_range_low.toLocaleString()}
                </p>
              </div>
              <div className="text-center p-3 bg-green-600 text-white rounded-lg">
                <p className="text-xs mb-1">Suggested</p>
                <p className="text-lg font-bold">
                  ${compData.market_analysis.your_home.suggested_list_price.toLocaleString()}
                </p>
              </div>
              <div className="text-center p-3 bg-white rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Optimistic</p>
                <p className="text-lg font-bold text-gray-800">
                  ${compData.market_analysis.your_home.price_range_high.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-2 mb-4">
              <DollarSign className="w-5 h-5 text-green-700" />
              <span className="text-lg font-semibold text-green-700">
                ${compData.market_analysis.avg_price_per_sqft} per sq ft average
              </span>
            </div>

            {onPriceSelect && (
              <Button
                onClick={() => handleUsePrice(compData.market_analysis.your_home.suggested_list_price)}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                Use Suggested Price
              </Button>
            )}
          </Card>

          {/* Market Insights */}
          <Card className="p-6 border-2 border-blue-200">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Market Insights</h4>
            <ul className="space-y-2">
              {compData.recommendations.map((rec, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-orange-600 rounded-full mt-1.5 flex-shrink-0"></div>
                  <p className="text-sm text-gray-700">{rec}</p>
                </li>
              ))}
            </ul>
          </Card>

          {/* Comparable Sales */}
          <Card className="p-6 border-2 border-gray-200">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Recent Comparable Sales</h4>
            <div className="space-y-3">
              {compData.comparables.map((comp, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{comp.address}</p>
                      <p className="text-xs text-gray-500">
                        {comp.beds} bed • {comp.baths} bath • {comp.square_feet.toLocaleString()} sqft
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-700">
                        ${comp.sale_price.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        ${comp.price_per_sqft}/sqft
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Sold: {comp.sale_date}</span>
                    <span>{comp.distance_miles} miles away</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PriceComparison;
